import { ListSubheader, MenuItem, Typography } from '@material-ui/core'
import { Controller, FieldErrors } from 'react-hook-form'
import { useUpdateEffect } from 'usehooks-ts'

import Button from '@/components/button'
import InputText from '@/components/input-text'
import useAccessControl from '@/hooks/use-access-control'

import { StepKeys } from '../..'
import { useNewActivityForm } from '../../new-activity-form'
import { MappingActivityForm } from '../../new-activity-form-schema'
import { useStyles } from './styles'

const temporaryTreatmentFilters = ['weed']

export interface MappingInfoStepProps {
  onChangeCurrentStep: (step: StepKeys) => void
}

export function MappingInfoStep({ onChangeCurrentStep }: MappingInfoStepProps) {
  const classes = useStyles()
  const { register, resetField, watch, control, formState } =
    useNewActivityForm()

  const errors = formState.errors as FieldErrors<MappingActivityForm>
  const { isSubmitting } = formState

  const [culture, treatment] = watch(['culture', 'treatment'])

  const { accessControl, currentCulture } = useAccessControl({
    treatment,
    culture,
  })

  function handleCancel() {
    onChangeCurrentStep('activity_type')
  }

  useUpdateEffect(() => {
    resetField('treatment', {
      defaultValue: '' as MappingActivityForm['treatment'],
    })
  }, [culture])

  return (
    <div className={classes.container}>
      <Typography variant='h1'>Informações básicas</Typography>

      <div className={classes.inputsContainer}>
        <InputText
          id='diagnosis_name'
          required
          label={'Nome do diagnóstico'}
          error={!!errors.diagnosis_name}
          helperText={errors.diagnosis_name?.message}
          {...register('diagnosis_name')}
        />

        <Controller
          name='culture'
          control={control}
          defaultValue={'' as MappingActivityForm['culture']}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <InputText
              id='culture'
              select
              required
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                  classes: { list: classes.menuList },
                },
              }}
              value={value}
              onChange={(e) => {
                onChange(e.target.value)
              }}
              label={'Selecione a cultura'}
              error={!!error}
              helperText={error?.message}
            >
              <ListSubheader value=''>Selecione a cultura</ListSubheader>

              {Object.entries(accessControl).map(
                ([culture_key, culture]) =>
                  culture?.enabled && (
                    <MenuItem value={culture_key} key={culture_key}>
                      {culture?.label}
                    </MenuItem>
                  ),
              )}
            </InputText>
          )}
        />

        <Controller
          name='treatment'
          control={control}
          defaultValue={'' as MappingActivityForm['treatment']}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <InputText
              id='treatment'
              select
              required
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                  classes: { list: classes.menuList },
                },
              }}
              value={value}
              onChange={(e) => {
                onChange(e.target.value)
              }}
              label={'Selecione o diagnóstico'}
              error={!!error}
              helperText={error?.message}
            >
              <ListSubheader value=''>Diagnóstico</ListSubheader>
              {currentCulture &&
                Object.entries(currentCulture.treatments)
                  .filter(([treatment_key]) =>
                    temporaryTreatmentFilters.includes(treatment_key),
                  )
                  .map(
                    ([treatment_key, treatment]) =>
                      treatment?.enabled && (
                        <MenuItem value={treatment_key} key={treatment_key}>
                          {treatment?.label}
                        </MenuItem>
                      ),
                  )}
            </InputText>
          )}
        />

        <div className={classes.buttonsContainer}>
          <Button outlined disabled={isSubmitting} onClick={handleCancel}>
            Cancelar
          </Button>
          <Button loading={isSubmitting} type='submit'>
            Avançar
          </Button>
        </div>
      </div>
    </div>
  )
}
